/**
 * SimpleRichTextEditor
 * --------------------
 * Lightweight contentEditable-Editor mit Toolbar (Fett, Kursiv, Listen, Link).
 * Ohne externe Dependency — gut genug für Vorab-Info-Texte, in denen wir
 * keine Bilder/Tabellen brauchen.
 *
 * Props:
 *   value         (string)  – HTML-Markup
 *   onChange      (fn)      – wird mit dem neuen HTML aufgerufen
 *   placeholder   (string)  – Platzhalter-Text wenn leer
 *   onInsertToken (fn)      – optional: wird vom Parent gerufen,
 *                             um einen `{token}` einzufügen
 */
import React, { useEffect, useRef } from 'react';
import { Bold, Italic, List, ListOrdered, Link as LinkIcon, Underline } from 'lucide-react';

export const SimpleRichTextEditor = React.forwardRef(function SimpleRichTextEditor(
  { value, onChange, placeholder, dataTestId = 'rich-text-editor' },
  ref
) {
  const editorRef = useRef(null);
  React.useImperativeHandle(ref, () => editorRef.current);

  // Sync external value → editor (only when it actually differs to avoid
  // losing the caret on every keystroke).
  useEffect(() => {
    if (editorRef.current && (value || '') !== editorRef.current.innerHTML) {
      editorRef.current.innerHTML = value || '';
    }
  }, [value]);

  const exec = (cmd, arg) => {
    document.execCommand(cmd, false, arg);
    editorRef.current?.focus();
    onChange?.(editorRef.current?.innerHTML || '');
  };

  const insertLink = () => {
    const url = window.prompt('URL (z.B. https://app.strongfit.ch):');
    if (url) exec('createLink', url);
  };

  return (
    <div className="border rounded-md bg-white" data-testid={dataTestId}>
      <div className="flex items-center gap-1 border-b px-2 py-1 bg-gray-50">
        <ToolbarBtn onClick={() => exec('bold')} title="Fett (⌘B)"><Bold size={14} /></ToolbarBtn>
        <ToolbarBtn onClick={() => exec('italic')} title="Kursiv (⌘I)"><Italic size={14} /></ToolbarBtn>
        <ToolbarBtn onClick={() => exec('underline')} title="Unterstrichen"><Underline size={14} /></ToolbarBtn>
        <div className="w-px h-5 bg-gray-300 mx-1" />
        <ToolbarBtn onClick={() => exec('insertUnorderedList')} title="Aufzählung"><List size={14} /></ToolbarBtn>
        <ToolbarBtn onClick={() => exec('insertOrderedList')} title="Nummerierte Liste"><ListOrdered size={14} /></ToolbarBtn>
        <ToolbarBtn onClick={insertLink} title="Link einfügen"><LinkIcon size={14} /></ToolbarBtn>
      </div>
      <div
        ref={editorRef}
        contentEditable
        suppressContentEditableWarning
        onInput={(e) => onChange?.(e.currentTarget.innerHTML)}
        onBlur={(e) => onChange?.(e.currentTarget.innerHTML)}
        className="min-h-[140px] p-3 text-sm outline-none prose prose-sm max-w-none"
        data-placeholder={placeholder}
        style={{ wordBreak: 'break-word' }}
      />
      <style>{`
        [contenteditable][data-placeholder]:empty::before {
          content: attr(data-placeholder);
          color: #9ca3af;
          pointer-events: none;
        }
        [contenteditable] ul { list-style: disc; padding-left: 22px; margin: 4px 0; }
        [contenteditable] ol { list-style: decimal; padding-left: 22px; margin: 4px 0; }
        [contenteditable] strong { font-weight: 700; }
        [contenteditable] em { font-style: italic; }
        [contenteditable] a { color: #2563eb; text-decoration: underline; }
      `}</style>
    </div>
  );
});

const ToolbarBtn = ({ onClick, title, children }) => (
  <button
    type="button"
    onMouseDown={(e) => e.preventDefault()}
    onClick={onClick}
    title={title}
    className="p-1.5 rounded hover:bg-gray-200 text-gray-700"
  >
    {children}
  </button>
);

export default SimpleRichTextEditor;
